import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { usePsaStatus } from "../hooks/usePsaStatus";
import { useUserMsp } from "../hooks/useUserMsp";
import { useIntegration } from "../hooks/useIntegration";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppDispatch } from "../hooks";
import SupervisorAction from "../actions/supervisorAction";
import SupervisorApi from "../apis/supervisorApi";
import NeoConfigModal from "./NeoConfigModal";
import { onboardingApi } from "../apis/onboardingApi";
import { getTicketScopeLabelPlural } from "../utils/mspUtils";
import AvailableIntegrationCard from "./AvailableIntegrationCard";
import { getConnectorDescription, getConnectorLogo } from "../utils/connectorUtils";
import { canManageTheoUsers, normalizeHorizonRole } from "../utils/horizonRoles";
import AutotaskSetupForm from "./AutotaskSetupForm";
import HaloSetupForm from "./HaloSetupForm";
import ConnectWiseSetupForm from "./ConnectWiseSetupForm";
import SuperOpsSetupForm from "./SuperOpsSetupForm";

type PsaType = "autotask" | "superops" | "connectwise" | "halo" | null;

/** Same catalog cards as Integrations. SuperOps is last. */
const PSA_SETUP_OPTIONS: Array<{ type: Exclude<PsaType, null>; name: string }> = [
  { type: "autotask", name: "autotask" },
  { type: "connectwise", name: "connectwise" },
  { type: "halo", name: "halo" },
  { type: "superops", name: "superops" },
];

const resolveSelection = (
  selectedIds: string[],
  allItems: Array<{ name: string; groupId: string }>
): Array<{ id: string; name: string }> =>
  selectedIds.map((id) => ({
    id,
    name: allItems.find((item) => item.groupId === id)?.name || id,
  }));

const SelectionSummary: React.FC<{
  title: string;
  items: Array<{ id: string; name: string }>;
  emptyLabel: string;
}> = ({ title, items, emptyLabel }) => (
  <Box
    sx={{
      border: "1px solid #E5E7EB",
      borderRadius: "8px",
      p: 1.5,
      background: "#FAFBFC",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontFamily: "var(--theo-font-sans)",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "#666F8D",
        mb: 1,
      }}
    >
      {items.length > 0 ? `${title} · ${items.length}` : title}
    </Typography>
    {items.length === 0 ? (
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#6c6c6c" }}>
        {emptyLabel}
      </Typography>
    ) : (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {items.map((item) => (
          <Chip
            key={item.id}
            label={item.name}
            size="small"
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: 13,
              height: "auto",
              background: "#F4EFE7",
              color: "#3d342b",
              "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
            }}
          />
        ))}
      </Box>
    )}
  </Box>
);

export interface PsaOnboardingSelections {
  /** PSA customer ids picked in advanced settings; empty/omitted imports all. */
  customerPsaIds?: string[];
}

interface PsaSetupScreenProps {
  onSetupComplete?: (selections?: PsaOnboardingSelections) => void;
}

const PsaSetupScreen: React.FC<PsaSetupScreenProps> = ({ onSetupComplete }) => {
  const { psaStatus } = usePsaStatus();
  const { userMspInfo } = useUserMsp();
  const { user } = useAuth0();
  const dispatch = useAppDispatch();
  const { platforms, getPlatforms, updatePlatformConfig } = useIntegration();
  const [selectedPsa, setSelectedPsa] = useState<PsaType>(null);
  /** After credentials are saved during onboarding, offer advanced settings before completing. */
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [credentialsSavedForPlatform, setCredentialsSavedForPlatform] = useState<string | null>(null);
  // Advanced settings: queue subset (persisted to platform metadata immediately)
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [allQueues, setAllQueues] = useState<Array<{ name: string; groupId: string }>>([]);
  const [selectedQueues, setSelectedQueues] = useState<string[]>([]);
  const [queueModalLoading, setQueueModalLoading] = useState(false);
  const [queueModalError, setQueueModalError] = useState<string | null>(null);
  const [queueModalSuccess, setQueueModalSuccess] = useState<boolean | null>(null);
  const [queueFetchError, setQueueFetchError] = useState<string | null>(null);
  // Advanced settings: customer subset (imported when onboarding continues)
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [allCustomers, setAllCustomers] = useState<Array<{ name: string; groupId: string }>>([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [customerModalLoading, setCustomerModalLoading] = useState(false);
  const [customerModalError, setCustomerModalError] = useState<string | null>(null);
  const [customerModalSuccess, setCustomerModalSuccess] = useState<boolean | null>(null);
  const [customerFetchError, setCustomerFetchError] = useState<string | null>(null);

  // ADMIN and ROOT can connect a PSA. Unknown until status loads, so don't
  // lock the button on a missing role — only a known non-admin is blocked.
  const isTechnician = normalizeHorizonRole(psaStatus?.userRole) === "TECHNICIAN";
  const canConfigurePsa = psaStatus ? canManageTheoUsers(psaStatus.userRole) : true;

  const handlePsaSelect = (psaType: PsaType) => {
    if (!canConfigurePsa) return;
    setSelectedPsa(psaType);
  };

  const handleBack = () => {
    setSelectedPsa(null);
  };

  const isOnboarding = Boolean(onSetupComplete);

  const platformForSelection = credentialsSavedForPlatform
    ? (platforms || []).find(
        (p: { name?: string; type?: string }) =>
          String(p.type || "").toLowerCase() === "psa" &&
          p.name?.toLowerCase() === credentialsSavedForPlatform?.toLowerCase()
      )
    : null;
  const sourceLabelPlural = getTicketScopeLabelPlural(
    credentialsSavedForPlatform
  ).toLowerCase();
  const sourceLabelPluralTitle =
    sourceLabelPlural.charAt(0).toUpperCase() + sourceLabelPlural.slice(1);

  useEffect(() => {
    if (credentialsSavedForPlatform && userMspInfo?.msp_id) {
      getPlatforms(userMspInfo.msp_id);
    }
  }, [credentialsSavedForPlatform, userMspInfo?.msp_id, getPlatforms]);

  /**
   * Credentials are saved; by default all queues and customers are imported.
   * Show the advanced-settings panel instead of completing immediately.
   */
  const handleCredentialsSaved = (platformName: string) => {
    setCredentialsSavedForPlatform(platformName);
    setAdvancedOpen(false);
    setSelectedQueues([]);
    setSelectedCustomerIds([]);
    setQueueFetchError(null);
    setCustomerFetchError(null);
  };

  const handleOpenQueueModal = async () => {
    if (!credentialsSavedForPlatform) return;
    setQueueFetchError(null);
    try {
      const api = new SupervisorApi();
      const response = await api.getAllTechnicianGroups(credentialsSavedForPlatform);
      const list = (Array.isArray(response) ? response : []).map(
        (q: { name?: string; groupId?: string | number; id?: string | number }) => ({
          name: q.name ?? "",
          groupId: String(q.groupId ?? q.id ?? ""),
        })
      );
      setAllQueues(list);
      const existingQueueIds = Array.isArray((platformForSelection as any)?.metadata?.queues)
        ? (platformForSelection as any).metadata.queues.map((id: number | string) => String(id))
        : [];
      setSelectedQueues(existingQueueIds);
      setQueueModalOpen(true);
      setQueueModalError(null);
      setQueueModalSuccess(null);
    } catch (err: any) {
      setQueueFetchError(
        err?.response?.data?.error || err?.message || `Failed to load ${sourceLabelPlural}`
      );
    }
  };

  const handleQueueModalClose = () => {
    setQueueModalOpen(false);
    setQueueModalError(null);
    setQueueModalSuccess(null);
  };

  const handleQueueSave = async () => {
    if (!platformForSelection?.id) return;
    setQueueModalLoading(true);
    setQueueModalError(null);
    try {
      await updatePlatformConfig(platformForSelection.id.toString(), {
        queues: selectedQueues,
      });
      if (user?.email) {
        dispatch(SupervisorAction.getTechnicianGroupList(user.email));
      }
      setQueueModalSuccess(true);
      setTimeout(() => {
        handleQueueModalClose();
      }, 700);
    } catch (err: any) {
      setQueueModalError(
        err?.message || `Failed to save ${sourceLabelPlural.slice(0, -1)} selection`
      );
    } finally {
      setQueueModalLoading(false);
    }
  };

  const handleOpenCustomerModal = async () => {
    if (!userMspInfo?.msp_id) return;
    setCustomerFetchError(null);
    try {
      const customers = await onboardingApi.getPsaCustomers(userMspInfo.msp_id);
      const list = (customers || [])
        .filter((c) => c.id && c.name)
        .map((c) => ({
          name: String(c.name!),
          groupId: String(c.id!),
        }));
      setAllCustomers(list);
      setCustomerModalOpen(true);
      setCustomerModalError(null);
      setCustomerModalSuccess(null);
    } catch (err: any) {
      setCustomerFetchError(
        err?.response?.data?.error || err?.message || "Failed to load customers"
      );
    }
  };

  const handleCustomerModalClose = () => {
    setCustomerModalOpen(false);
    setCustomerModalError(null);
    setCustomerModalSuccess(null);
  };

  /**
   * Customer selection is applied when onboarding continues (subset import).
   * Nothing is persisted until Continue.
   */
  const handleCustomerSave = async () => {
    setCustomerModalLoading(true);
    setCustomerModalError(null);
    try {
      setCustomerModalSuccess(true);
      setTimeout(() => {
        handleCustomerModalClose();
      }, 700);
    } finally {
      setCustomerModalLoading(false);
    }
  };

  const handleContinueAfterAdvancedSettings = () => {
    const selections =
      selectedCustomerIds.length > 0 ? { customerPsaIds: selectedCustomerIds } : undefined;
    setCredentialsSavedForPlatform(null);
    setAdvancedOpen(false);
    setSelectedPsa(null);
    setQueueFetchError(null);
    setCustomerFetchError(null);
    onSetupComplete?.(selections);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "calc(100vh - 84px)",
        background: "#f8fafc",
        p: 3,
        pt: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 800,
          width: "100%",
          p: 4,
          borderRadius: 2,
          background: "#fff",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 700,
            mb: 2,
            color: "#1a1a1a",
          }}
        >
          PSA Setup Required
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            mb: 4,
            color: "#6c6c6c",
            lineHeight: 1.6,
          }}
        >
          To continue using the platform, you need to set up a PSA (Professional Services Automation) integration. 
          Please select your PSA platform below.
        </Typography>

        {isTechnician && (
          <Alert
            severity="info"
            sx={{
              mb: 4,
              fontFamily: "var(--theo-font-sans)",
              "& .MuiAlert-message": {
                fontFamily: "var(--theo-font-sans)",
              },
            }}
          >
            Contact your administrator to set up the PSA integration.
          </Alert>
        )}

        {credentialsSavedForPlatform && isOnboarding ? (
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Satoshi",
                fontWeight: 600,
                mb: 2,
                color: "#1a1a1a",
              }}
            >
              PSA connected
            </Typography>
            <Typography
              sx={{
                fontFamily: "Satoshi",
                color: "#6c6c6c",
                mb: 2,
              }}
            >
              All {sourceLabelPlural} and customers are imported by default. Continue, or
              narrow the scope first in advanced settings.
            </Typography>
            {queueFetchError && (
              <Alert severity="error" onClose={() => setQueueFetchError(null)} sx={{ mb: 2, fontFamily: "Satoshi" }}>
                {queueFetchError}
              </Alert>
            )}
            {customerFetchError && (
              <Alert severity="error" onClose={() => setCustomerFetchError(null)} sx={{ mb: 2, fontFamily: "Satoshi" }}>
                {customerFetchError}
              </Alert>
            )}
            <Button
              fullWidth
              onClick={() => setAdvancedOpen((v) => !v)}
              sx={{
                fontFamily: "Satoshi",
                textTransform: "none",
                justifyContent: "space-between",
                color: "#705d4a",
                border: "1px solid #e5e5e5",
                mb: 2,
                "&:hover": { bgcolor: "#f9f9f9", borderColor: "#705d4a" },
              }}
            >
              Advanced settings (optional)
              {advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Button>
            <Collapse in={advancedOpen}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    onClick={handleOpenQueueModal}
                    sx={{
                      fontFamily: "Satoshi",
                      textTransform: "none",
                      fontWeight: 500,
                      borderColor: "#705d4a",
                      color: "#705d4a",
                      "&:hover": { borderColor: "#604d3a", background: "#f5f5f5" },
                    }}
                  >
                    {`Select ${sourceLabelPluralTitle}`}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleOpenCustomerModal}
                    sx={{
                      fontFamily: "Satoshi",
                      textTransform: "none",
                      fontWeight: 500,
                      borderColor: "#705d4a",
                      color: "#705d4a",
                      "&:hover": { borderColor: "#604d3a", background: "#f5f5f5" },
                    }}
                  >
                    Select customers
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  <SelectionSummary
                    title={sourceLabelPluralTitle}
                    items={resolveSelection(selectedQueues, allQueues)}
                    emptyLabel="All are imported"
                  />
                  <SelectionSummary
                    title="Customers"
                    items={resolveSelection(selectedCustomerIds, allCustomers)}
                    emptyLabel="All are imported"
                  />
                </Box>
                <Typography sx={{ fontFamily: "Satoshi", color: "#9e9e9e", fontSize: 12 }}>
                  Leaving a section untouched imports everything in it.
                </Typography>
              </Box>
            </Collapse>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={handleContinueAfterAdvancedSettings}
                sx={{
                  fontFamily: "Satoshi",
                  background: "#705d4a",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": { background: "#604d3a" },
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        ) : selectedPsa === "autotask" ? (
          <AutotaskSetupForm
            onBack={handleBack}
            onSuccess={onSetupComplete}
            onCredentialsSaved={isOnboarding ? handleCredentialsSaved : undefined}
          />
        ) : selectedPsa === "superops" ? (
          <SuperOpsSetupForm
            onBack={handleBack}
            onSuccess={onSetupComplete}
            onCredentialsSaved={isOnboarding ? handleCredentialsSaved : undefined}
          />
        ) : selectedPsa === "connectwise" ? (
          <ConnectWiseSetupForm
            onBack={handleBack}
            onSuccess={onSetupComplete}
            onCredentialsSaved={isOnboarding ? handleCredentialsSaved : undefined}
          />
        ) : selectedPsa === "halo" ? (
          <HaloSetupForm
            onBack={handleBack}
            onSuccess={onSetupComplete}
            onCredentialsSaved={isOnboarding ? handleCredentialsSaved : undefined}
          />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mt: 4,
              alignItems: "stretch",
            }}
          >
            {PSA_SETUP_OPTIONS.map((option) => (
              <AvailableIntegrationCard
                key={option.type}
                logo={getConnectorLogo(option.name)}
                name={option.name}
                description={getConnectorDescription(option.name)}
                connectLabel="Connect"
                disabled={!canConfigurePsa}
                onConnect={() => handlePsaSelect(option.type)}
              />
            ))}
          </Box>
        )}

        <NeoConfigModal
          open={queueModalOpen}
          items={allQueues}
          selectedItemIds={selectedQueues}
          loading={queueModalLoading}
          error={queueModalError}
          success={queueModalSuccess}
          onSelect={setSelectedQueues}
          onClose={handleQueueModalClose}
          onConnect={handleQueueSave}
          appName={credentialsSavedForPlatform ?? "PSA"}
          itemLabelPlural={sourceLabelPlural}
          selectionHeading={`Select ${sourceLabelPlural} to import`}
          selectionDescription={`Only tickets in the selected ${sourceLabelPlural} will show in Theo. Leave everything unselected to import all.`}
          searchable
          searchPlaceholder={`Search ${sourceLabelPlural}...`}
        />
        <NeoConfigModal
          open={customerModalOpen}
          items={allCustomers}
          selectedItemIds={selectedCustomerIds}
          loading={customerModalLoading}
          error={customerModalError}
          success={customerModalSuccess}
          onSelect={setSelectedCustomerIds}
          onClose={handleCustomerModalClose}
          onConnect={handleCustomerSave}
          appName={credentialsSavedForPlatform ?? "PSA"}
          itemLabelPlural="customers"
          selectionHeading="Select customers to import"
          selectionDescription="Only the selected customers will be imported as Theo customers. Leave everything unselected to import all."
          searchable
          searchPlaceholder="Search customers..."
        />
      </Paper>
    </Box>
  );
};

export default PsaSetupScreen;
